import { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Plus, Pin, Trash2, Pencil, DollarSign, Share2, Loader2, LayoutGrid, List, ChevronDown, ChevronRight } from 'lucide-react';
// Layout mirrors TasksPage.js header (Select All + Group toggle + View toggle + New button)
import { useEscapeToHome } from '@/hooks/useKeyboard';
import { SearchableSelect } from '@/components/SearchableSelect';

export default function ExpensesPage() {
  const { activeProject, projects } = useApp();
  useEscapeToHome();
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [viewMode, setViewMode] = useState('list'); // list | board
  const [groupBy, setGroupBy] = useState('category'); // category | method | workspace
  const [collapsedListGroups, setCollapsedListGroups] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareUsername, setShareUsername] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [selected, setSelected] = useState(new Set());

  const [form, setForm] = useState({
    title: '', project_id: '', amount: '', category: 'General',
    payment_method: 'Cash', date: new Date().toISOString().slice(0, 10),
    description: '', tags: ''
  });

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = { project_id: activeProject, page, limit };
      if (categoryFilter) params.category = categoryFilter;
      const { data } = await api.get('/expenses', { params });
      setExpenses(data.expenses);
      setTotal(data.total);
    } catch {}
    setLoading(false);
  }, [activeProject, page, limit, categoryFilter]);

  const fetchCategories = useCallback(async () => {
    try { const { data } = await api.get('/expense-categories'); setCategories(data.categories); } catch {}
  }, []);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { setSelected(new Set()); }, [expenses]);

  // 'N' shortcut to create new
  useEffect(() => {
    const h = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const isTyping = ['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName) || e.target.getAttribute('role') === 'combobox';
      if (e.key === 'n' && !isTyping) {
        e.preventDefault();
        setEditing(null); setForm({ title: '', project_id: activeProject === 'all' ? '' : activeProject, amount: '', category: 'General', payment_method: 'Cash', date: new Date().toISOString().slice(0, 10), description: '', tags: '' });
        setShowForm(true);
      } else if (e.key === 'v' && !isTyping) {
        e.preventDefault();
        setViewMode(m => m === 'board' ? 'list' : 'board');
      } else if (e.key === 'g' && !isTyping) {
        e.preventDefault();
        setGroupBy(g => g === 'category' ? 'method' : g === 'method' ? 'workspace' : 'category');
      }
    };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  });

  const handleFormKeyDown = (e, fieldIndex, totalFields) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (fieldIndex >= totalFields - 1) { save(); }
      else { focusFormField(fieldIndex + 1); }
    }
  };

  const focusFormField = (idx) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-form-idx="${idx}"]`);
      if (el) {
        el.focus();
        if (el.getAttribute('role') === 'combobox') el.click();
      }
    }, 60);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', project_id: activeProject === 'all' ? '' : activeProject, amount: '', category: 'General', payment_method: 'Cash', date: new Date().toISOString().slice(0, 10), description: '', tags: '' });
    setShowForm(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({ title: e.title, project_id: e.project_id, amount: String(e.amount), category: e.category, payment_method: e.payment_method || 'Cash', date: e.date || '', description: e.description || '', tags: (e.tags || []).join(', ') });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.title || !form.project_id || !form.amount) { toast.error('Title, workspace, and amount are required'); return; }
    const payload = { ...form, amount: parseFloat(form.amount), tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
    try {
      if (editing) { await api.put(`/expenses/${editing.id}`, payload); toast.success('Expense updated'); }
      else { await api.post('/expenses', payload); toast.success('Expense created'); }
      setShowForm(false); fetchExpenses(); fetchCategories();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Move this expense to trash?')) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast('Expense moved to trash');
      fetchExpenses();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const togglePin = async (id) => { try { await api.put(`/pin/expense/${id}`); fetchExpenses(); } catch {} };

  const shareRecord = async () => {
    if (!shareUsername) { toast.error('Enter a username'); return; }
    try {
      await api.post('/share', { record_type: 'expense', record_id: shareTarget.id, shared_with_username: shareUsername, permission: sharePermission });
      toast.success('Expense shared'); setShowShare(false); setShareUsername('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleSelect = (id) => { setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} expense(s)?`)) return;
    try { await api.post('/bulk-delete', { record_type: 'expense', record_ids: [...selected] }); toast.success(`${selected.size} expenses moved to trash`); setSelected(new Set()); fetchExpenses(); }
    catch { toast.error('Bulk delete failed'); }
  };

  const selectAllExpenses = () => {
    if (expenses.length > 0 && expenses.every(e => selected.has(e.id))) setSelected(new Set());
    else setSelected(new Set(expenses.map(e => e.id)));
  };

  const projectMap = {}; projects.forEach(p => { projectMap[p.id] = p.name; });
  const totalAmount = expenses.reduce((s, e) => s + e.amount, 0);
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden -mb-20 md:-mb-6" data-testid="expenses-page">
      <div className="shrink-0 p-4 md:px-8 md:pt-6 md:pb-4 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl sm:text-3xl font-medium tracking-tight">Expenses</h2>
            <p className="text-sm text-zinc-500 mt-1">{total} expense{total !== 1 ? 's' : ''} &middot; Total: <span className="font-mono font-medium text-zinc-900 dark:text-zinc-100">{totalAmount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span></p>
          </div>
          <div className="flex items-center gap-2">
            {/* Select All */}
            {viewMode === 'list' && expenses.length > 0 && (
              <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer mr-2" data-testid="select-all-expenses">
                <Checkbox checked={expenses.length > 0 && expenses.every(e => selected.has(e.id))} onCheckedChange={selectAllExpenses} className="h-3.5 w-3.5" />
                <span>All</span>
              </label>
            )}
            {/* Group by toggle */}
            <div className="hidden sm:flex items-center gap-1 text-xs text-zinc-400 mr-2">
              <span>Group:</span>
              <button onClick={() => setGroupBy('category')} className={`px-2 py-1 rounded ${groupBy === 'category' ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 font-medium' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'}`} data-testid="expenses-group-by-category">Category</button>
              <button onClick={() => setGroupBy('method')} className={`px-2 py-1 rounded ${groupBy === 'method' ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 font-medium' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'}`} data-testid="expenses-group-by-method">Method</button>
              <button onClick={() => setGroupBy('workspace')} className={`px-2 py-1 rounded ${groupBy === 'workspace' ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 font-medium' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'}`} data-testid="expenses-group-by-workspace">Workspace</button>
            </div>
            {/* View toggle */}
            <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5">
              <button onClick={() => setViewMode('board')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'board' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="expenses-view-board">
                <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.5} /> Board</button>
              <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="expenses-view-list">
                <List className="h-3.5 w-3.5" strokeWidth={1.5} /> List</button>
            </div>
            <Button onClick={openCreate} className="rounded-xl" data-testid="create-expense-btn">
              <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> New Expense
            </Button>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 md:px-8">
        <div className="w-full">

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
      ) : expenses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <img src="https://static.prod-images.emergentagent.com/jobs/89959381-8fb5-45c9-9dbb-0dfb477e8d4d/images/3aef46e34a35980c9bb9d820a9a836a3c330b826e071b83d88fbfb4794886dd4.png"
            alt="Empty" className="w-48 h-48 opacity-50 mb-6" />
          <p className="text-zinc-500 text-sm">No expenses found. Track your first expense.</p>
        </div>
      ) : viewMode === 'board' ? (
        <>
          {(() => {
            const groups = {};
            expenses.forEach(e => {
              const k = groupBy === 'category' ? (e.category || 'Uncategorized')
                      : groupBy === 'method' ? (e.payment_method || 'Unspecified')
                      : (projectMap[e.project_id] || 'No Workspace');
              (groups[k] = groups[k] || []).push(e);
            });
            const keys = Object.keys(groups).sort();
            return (
              <div className="flex gap-3 overflow-x-auto pb-4" data-testid="expenses-board-view">
                {keys.map(k => {
                  const items = groups[k];
                  const colTotal = items.reduce((s, x) => s + x.amount, 0);
                  return (
                    <div key={k} className="flex-shrink-0 w-[calc((100vw-3rem)/2)] sm:w-[calc((100vw-4rem)/3)] md:w-0 md:flex-1 md:min-w-[220px] flex flex-col rounded-xl bg-zinc-50 dark:bg-zinc-900/50" data-testid={`expense-col-${k}`}>
                      <div className="flex items-center justify-between gap-2 p-3 border-b border-zinc-200 dark:border-zinc-800">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
                          <span className="text-xs font-bold uppercase tracking-wider truncate">{k}</span>
                          <span className="text-xs text-zinc-400">{items.length}</span>
                        </div>
                        <span className="text-[11px] font-mono font-medium">{colTotal.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</span>
                      </div>
                      <div className="flex-1 overflow-y-auto p-2 space-y-2">
                        {items.map(e => (
                          <div key={e.id} className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-2.5 hover:shadow-md transition-shadow group relative"
                            data-testid={`expense-board-card-${e.id}`}>
                            <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 bg-white dark:bg-zinc-900 rounded shadow-sm border border-zinc-200 dark:border-zinc-800 p-0.5">
                              <button onClick={() => { setShareTarget(e); setShowShare(true); }} className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"><Share2 className="h-3 w-3 text-zinc-500" /></button>
                              <button onClick={() => openEdit(e)} className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"><Pencil className="h-3 w-3 text-zinc-500" /></button>
                              <button onClick={() => deleteExpense(e.id)} className="p-1 hover:bg-red-50 dark:hover:bg-red-950 rounded"><Trash2 className="h-3 w-3 text-red-400" /></button>
                            </div>
                            <p className="text-xs font-medium truncate pr-16">{e.title}</p>
                            <p className="text-[10px] text-zinc-400 mt-0.5">{e.date} · {projectMap[e.project_id] || '—'}</p>
                            <p className="font-mono text-sm font-medium mt-1">{e.amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </>
      ) : (
        <>
          {(() => {
            const groups = {};
            expenses.forEach(e => {
              const k = groupBy === 'category' ? (e.category || 'Uncategorized')
                      : groupBy === 'method' ? (e.payment_method || 'Unspecified')
                      : (projectMap[e.project_id] || 'No Workspace');
              (groups[k] = groups[k] || []).push(e);
            });
            const keys = Object.keys(groups).sort();
            return (
              <div className="space-y-4">
                {keys.map(k => {
                  const items = groups[k];
                  const colTotal = items.reduce((s, x) => s + x.amount, 0);
                  const isCollapsed = collapsedListGroups.has(k);
                  return (
                    <div key={k} data-testid={`expense-list-group-${k}`}>
                      <button onClick={() => setCollapsedListGroups(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; })}
                        className="flex items-center gap-2 w-full text-left py-1.5 px-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-900" data-testid={`expense-toggle-group-${k}`}>
                        {isCollapsed ? <ChevronRight className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
                        <Badge className="bg-emerald-600 text-white text-[10px] px-2 py-0.5">{k}</Badge>
                        <span className="text-xs text-zinc-400">{items.length}</span>
                        <span className="ml-auto text-[11px] font-mono font-medium">{colTotal.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</span>
                      </button>
                      {!isCollapsed && (
                        <div className="border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden mt-2">
                          {items.map(e => (
                            <div key={e.id} className={`flex items-center justify-between p-3 border-b border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-950 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors group ${selected.has(e.id) ? 'bg-zinc-50 dark:bg-zinc-900' : ''}`}
                              data-testid={`expense-item-${e.id}`}>
                              <div className="flex items-center gap-3 min-w-0">
                                <Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggleSelect(e.id)} className="h-4 w-4 shrink-0" data-testid={`expense-select-${e.id}`} />
                                <div className="w-10 h-10 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0">
                                  <DollarSign className="h-5 w-5 text-zinc-500" strokeWidth={1.5} />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium truncate">{e.title}</span>
                                    <Badge variant="secondary" className="text-[10px]">{e.category}</Badge>
                                  </div>
                                  <div className="flex items-center gap-3 mt-0.5">
                                    <span className="text-[11px] text-zinc-400">{projectMap[e.project_id] || 'Unknown'}</span>
                                    <span className="text-[11px] text-zinc-400">{e.date}</span>
                                    <span className="text-[11px] text-zinc-400">{e.payment_method}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                <span className="font-mono text-sm font-medium">{e.amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span>
                                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => togglePin(e.id)} data-testid={`pin-expense-${e.id}`}>
                                    <Pin className={`h-3.5 w-3.5 ${e.is_pinned ? 'fill-current text-zinc-900' : 'text-zinc-400'}`} strokeWidth={1.5} />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setShareTarget(e); setShowShare(true); }} data-testid={`share-expense-${e.id}`}>
                                    <Share2 className="h-3.5 w-3.5 text-zinc-400" strokeWidth={1.5} />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(e)} data-testid={`edit-expense-${e.id}`}>
                                    <Pencil className="h-3.5 w-3.5 text-zinc-400" strokeWidth={1.5} />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteExpense(e.id)} data-testid={`delete-expense-${e.id}`}>
                                    <Trash2 className="h-3.5 w-3.5 text-red-400" strokeWidth={1.5} />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-zinc-100 dark:border-zinc-800">
              <p className="text-sm text-zinc-400">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Previous</Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</Button>
              </div>
            </div>
          )}
        </>
      )}
      </div>
      </div>

      {/* Multi-select floating bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-20 md:bottom-8 left-1/2 -translate-x-1/2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-4 z-50" data-testid="expense-selection-bar">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <button onClick={clearSelection} className="text-xs underline opacity-70 hover:opacity-100" data-testid="expense-clear-selection">Clear</button>
          <Button size="sm" variant="destructive" onClick={bulkDelete} data-testid="expense-bulk-delete">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
          </Button>
        </div>
      )}

      {/* Expense Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="expense-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Expense' : 'New Expense'}</DialogTitle>
            <DialogDescription>{editing ? 'Update expense details' : 'Track a new expense'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Expense title" className="h-12" data-testid="expense-title-input" data-form-idx="0" tabIndex={0}
                onKeyDown={e => handleFormKeyDown(e, 0, 8)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Workspace *</Label>
                <SearchableSelect value={form.project_id} onValueChange={v => setForm(p => ({ ...p, project_id: v }))}
                  options={projects.map(p => ({ value: p.id, label: p.name }))} placeholder="Select workspace"
                  data-testid="expense-project-select" data-form-idx="1"
                  onMoveToNext={() => focusFormField(2)} />
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00" className="h-12 font-mono" data-testid="expense-amount-input" data-form-idx="2" tabIndex={0}
                  onKeyDown={e => handleFormKeyDown(e, 2, 8)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Category</Label>
                <Input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  placeholder="Type to create" list="categories" data-testid="expense-category-input" data-form-idx="3" tabIndex={0}
                  onKeyDown={e => handleFormKeyDown(e, 3, 8)} />
                <datalist id="categories">{categories.map(c => <option key={c} value={c} />)}</datalist>
              </div>
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <SearchableSelect value={form.payment_method} onValueChange={v => setForm(p => ({ ...p, payment_method: v }))}
                  options={[{value:'Cash',label:'Cash'},{value:'Card',label:'Card'},{value:'UPI',label:'UPI'},{value:'Bank Transfer',label:'Bank Transfer'},{value:'Other',label:'Other'}]}
                  placeholder="Payment" data-testid="expense-payment-select" data-form-idx="4"
                  onMoveToNext={() => focusFormField(5)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                  data-testid="expense-date-input" data-form-idx="5" tabIndex={0} onKeyDown={e => handleFormKeyDown(e, 5, 8)} />
              </div>
              <div className="space-y-2">
                <Label>Tags</Label>
                <Input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))}
                  placeholder="tag1, tag2" data-testid="expense-tags-input" data-form-idx="6" tabIndex={0}
                  onKeyDown={e => handleFormKeyDown(e, 6, 8)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Optional description" data-testid="expense-description-input" data-form-idx="7" tabIndex={0}
                onKeyDown={e => handleFormKeyDown(e, 7, 8)} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-xl" data-testid="expense-save-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent data-testid="expense-share-dialog">
          <DialogHeader>
            <DialogTitle>Share Expense</DialogTitle>
            <DialogDescription>Share "{shareTarget?.title}" with another user</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={shareUsername} onChange={e => setShareUsername(e.target.value)}
                placeholder="Enter username" data-testid="expense-share-username"
                onKeyDown={e => { if (e.key === 'Enter') shareRecord(); }} />
            </div>
            <div className="space-y-2">
              <Label>Permission</Label>
              <Select value={sharePermission} onValueChange={setSharePermission}>
                <SelectTrigger data-testid="expense-share-permission"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">View</SelectItem>
                  <SelectItem value="edit">Edit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={shareRecord} className="rounded-xl" data-testid="expense-share-submit">Share</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
